<template>

    <div class="pt-2">

        <v-alert v-if="!hasPermission('view projects')" type="error" class="body-2">You don't have permission to View
            Project
        </v-alert>

        <v-card v-if="hasPermission('view projects')"
                flat
                tile
                class="d-flex justify-end pl-3 pr-3 pt-2 pb-2">

            <v-tooltip top color="primary" v-for="filter in tableFilters"
                       :key="filter.type">

                <template v-slot:activator="{ on }">
                    <v-chip class="ml-2"
                            v-on="on"
                            close
                            pill
                            :active="filter.model"
                            @click:close="filterRemoved(filter)"
                    >
                        <v-avatar class="primary" left>
                            <v-icon color="white" small>mdi-filter</v-icon>
                        </v-avatar>
                        {{ filter.name }}
                    </v-chip>
                </template>
                <span>{{ filter.name }} is active!</span>
            </v-tooltip>

            <v-menu offset-y>
                <template v-slot:activator="{ on }">
                    <v-chip class="ml-2" pill v-on="on">
                        <v-avatar left color="primary">
                            <v-icon color="white">mdi-plus</v-icon>
                        </v-avatar>
                        Add filter
                    </v-chip>
                </template>

                <v-list>
                    <v-list-item
                        v-for="(item, index) in tableFilters"
                        :key="index"
                        @click="addFilter(item)"
                    >
                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>

            <div class="flex-grow-1">
            </div>

            <v-text-field
                v-model="tableSearch"
                prepend-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                clearable
                solo
                dense
                background-color="grey darken-2"
                class="mb-2"
                style="max-width: 400px;"
            />

            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn
                        :disabled="!hasPermission('view projects')"
                        @click="refresh()"
                        v-on="on"
                        icon>
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </template>
                <span>Reload data</span>
            </v-tooltip>

        </v-card>

        <!-- PROJECT WARNINGS -->
        <v-scale-transition>
            <v-alert type="warning"
                     prominent
                     v-show="warningText"
                     dense
                     icon="mdi-alert"
                     class="body-2 ml-2 mr-2">
                {{ warningText }}
            </v-alert>
        </v-scale-transition>

        <!-- ACTIVE TABLE -->
        <v-data-table
            v-if="projects"
            :headers="selectMode ? selectHeaders : headers"
            :items="getFilteredProjects"
            :show-select="selectMode === 'multiple'"
            :loading="loading"
            :options.sync="tableOptions"
            no-data-text="No projects found"
            no-results-text="No projects found"
            :search="tableSearch">

            <template v-slot:body="{ items }">

                <tbody>

                <!-- NO DATA INFO -->
                <tr v-if="items.length === 0">
                    <td colspan="100">
                        <v-alert
                            color="primary"
                            class="mt-4 body-2"
                            icon="mdi-information">
                            No projects found!
                        </v-alert>
                    </td>
                </tr>

                <table-row v-for="item in items" :key="item.id"
                           @edit="editProject"
                           @delete="deleteProject"
                           @clone="cloneProject"
                           @stats="openProjectStats"
                           @quotas="openQuotaStats"
                           @geostats="openProjectGeoStats"
                           @participants="openParticipants"
                           @disqualify="openDisqualifyDialog"
                           @select="projectSelected"
                           @deselect="projectDeselected"
                           :data="item"
                           :select-mode="selectMode"
                           :selected="selectedModel.has(item.id) ? item.id : null"
                           :columns="selectMode ? selectHeaders : headers"
                />

                </tbody>
            </template>

        </v-data-table>
    </div>

</template>

<script>

import {createNamespacedHelpers} from 'vuex';

const {mapGetters, mapMutations, mapState} = createNamespacedHelpers('ProjectManager');
import TableRow from './ListProjectsTableRow';
import Json from 'vue-json-pretty';

export default {

    components: {
        TableRow,
        Json
    },

    props: {

        projects: {
            type: Array,
            required: true,
        },

        loading: {
            type: Boolean,
            required: true,
        },

        selectMode: {
            type: String | null,
            required: false,
            default: null,
            validator: (value) => {
                return ['single', 'multiple'].includes(value);
            }
        },

        selected: {
            type: Array | null,
            required: false,
            default: () => []
        }
    },

    data() {
        return {

            warningText: null,

            statuses: [
                {status: 'online', name: 'Show active projects only'},
                {status: 'offline', name: 'Show projects offline'},
                {status: 'closed', name: 'Show closed projects'},
                {
                    name: 'progress_percent',
                    text: 'PROGRESS',
                    visible: true,
                    value: 'completed_percent',
                    width: '120px'
                },
            ],

            selectHeaders: [
                {name: 'name', text: 'NAME', visible: true, value: 'name', align: 'left'},
                {name: 'status', text: 'STATUS', visible: true, value: 'status', width: '100px'},
                {name: 'calendar', text: 'DEADLINE', width: '150px', visible: true, value: 'end_date'},
            ],

            headers: [
                {name: 'owner', text: 'OWNER', visible: true, sortable: false, width: '70px'},
                {name: 'name', text: 'PROJECT NAME', visible: true, value: 'name', align: 'left'},
                {name: 'progress_percent', text: 'PROGRESS', visible: true, value: 'completed_percent', width: '120px'},
                {name: 'status', text: 'STATUS', visible: true, value: 'status', width: '100px'},
                {name: 'dashboard', text: 'DASHBOARD', visible: true, sortable: false, align: 'center', width: '100px'},
                {name: 'response rate', text: 'RESPONSE RATE', visible: true, value: 'response_rate', width: '150px'},
                {name: 'incidence rate', text: 'INCIDENCE RATE', visible: true, value: 'incidence_rate', width: '150px'},
                {name: 'rating', text: 'RATING', visible: true, sortable: true, align: 'center', width: '100px'},
                {name: 'calendar', text: 'DEADLINE', width: '150px', visible: true, value: 'end_date'},
                {name: 'actions', text: '', visible: true, align: 'right', value: '', width: '40px', sortable: false},

                // These columns are part of the table but invisible -> this enables table search to use these
                {
                    name: 'extpid',
                    text: 'external_project',
                    value: 'external_project_reference',
                    visible: false,
                    sortable: false,
                    align: ' d-none'
                },
                {
                    name: 'extbid',
                    text: 'external_billing',
                    value: 'external_billing_reference',
                    visible: false,
                    sortable: false,
                    align: ' d-none'
                },
                {name: 'id', text: 'id', value: 'id', visible: false, sortable: false, align: ' d-none'}
            ],

            selectedModel: new Set()
        }
    },

    computed: {

        ...mapState([
            'activeTableFilters',
            'activeTableOptions',
            'activeTableSearch',
        ]),

        ...mapGetters([
            'getFilteredProjects',
            'getUserProjects'
        ]),

        tableOptions: {
            get() {
                return this.activeTableOptions;
            },
            set(options) {
                this.setActiveTableOptions(options);
            }
        },

        tableSearch: {
            get() {
                return this.activeTableSearch;
            },
            set(term) {
                this.setActiveTableSearch(term)
            }
        },

        tableFilters() {
            return this.activeTableFilters;
        },
    },

    watch: {
        getFilteredProjects(projects) {

            if (this.$route.name === 'project-manager-list') {
                this.findErrors();
            }
        },

        selected() {
            this.initializeSelected();
        }
    },

    methods: {

        ...mapMutations([
            'setActiveTableOptions',
            'setActiveTableSearch',
        ]),

        findErrors() {
            const errorFound = this.getFilteredProjects
                .filter(project => ['offline', 'online'].includes(project.status))
                .find(project => project.errors.length > 0);

            if (errorFound) {
                this.warningText = 'One or more projects contain errors! Please review them!';
            } else {
                this.warningText = null;
            }
        },

        addFilter(filter) {
            filter.model = true;
        },

        filterRemoved(filter) {
            filter.model = false
        },

        editProject(project) {
            if (this.selectMode) {
                this.$emit('select', project);
            } else {
                this.$emit('edit', project);
            }
        },

        deleteProject(projectId) {
            this.$emit('delete', projectId);
        },

        cloneProject(projectId) {
            this.$emit('clone', projectId);
        },

        openProjectStats(project) {
            this.$emit('stats', project);
        },

        openQuotaStats(project) {
            this.$emit('quotas', project);
        },

        openProjectGeoStats(project) {
            this.$emit('geostats', project);
        },

        openParticipants(project) {
            this.$emit('participants', project);
        },

        openDisqualifyDialog(project) {
            this.$emit('disqualify', project);
        },

        refresh() {
            this.$emit('refresh');
        },

        projectSelected(id) {
            this.selectedModel.add(id);
            this.$emit('selected', Array.from(this.selectedModel));
        },

        projectDeselected(id) {
            this.selectedModel.delete(id);
            this.$emit('selected', Array.from(this.selectedModel));
        },

        initializeSelected() {
            if (this.selected) {
                this.selectedModel = new Set(this.selected);
            }
        }
    },

    mounted() {
        Bus.$on('errors-cleared', () => {
            this.findErrors();
        });

        this.initializeSelected();
    }
}
</script>
